import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MAX_AUTHOR_NAME_LENGTH, MAX_PROJECT_TITLE_LENGTH } from '../const';
import { renameProject } from '../ducks/project';
import {
  projectAuthorSelector,
  projectTitleSelector,
} from '../reducers/selectors';
import { useModalState } from '../utils/useModalState';
import { Button } from './Button';
import { FormField } from './FormField';
import { FormFieldGroup } from './FormFieldGroup';
import { Modal } from './Modal';
import { useTranslation } from 'react-i18next';

export function ProjectDetailsModal({ show, onHide }) {
  const { t } = useTranslation();
  const { title, author, setTitle, setAuthor, dirty, valid } = useState(show);
  const dispatch = useDispatch();
  const disabled = useMemo(() => !valid, [valid]);
  const modified = useMemo(() => dirty, [dirty]);
  const onSubmit = useCallback(() => {
    if (modified) {
      dispatch(renameProject(title, author));
    }
    // TODO show loading state
    onHide();
  }, [dispatch, title, author]);

  return (
    <Modal
      show={show}
      title={t('AUTHORINFOMODAL.projectDetails')}
      onHide={onHide}
      disableHide={true}
      onSubmit={onSubmit}
      footer={
        <Button
          type="submit"
          width="182px"
          variant="primary"
          disabled={disabled}
        >
          {t('AUTHORINFOMODAL.continue')}
        </Button>
      }
    >
      <FormFieldGroup
        requiredFieldMessage={t('AUTHORINFOMODAL.requiredFieldMessage')}
      >
        <FormField
          type="text"
          label={t('AUTHORINFOMODAL.projectTitle')}
          value={title}
          onChange={setTitle}
          required
          maxLength={MAX_PROJECT_TITLE_LENGTH}
        />
        <FormField
          autoFocus={!author}
          type="text"
          label={t('AUTHORINFOMODAL.authorName')}
          value={author}
          onChange={setAuthor}
          required
          maxLength={MAX_AUTHOR_NAME_LENGTH}
        />
      </FormFieldGroup>
    </Modal>
  );
}

function useState(show) {
  const initialTitle = useSelector(projectTitleSelector);
  const initialAuthor = useSelector(projectAuthorSelector);

  const initialState = useMemo(
    () => ({
      title: initialTitle,
      author: initialAuthor,
    }),
    [initialTitle, initialAuthor],
  );

  const [state, setState] = useModalState(show, initialState);

  const dirty = useMemo(
    () => !isEqual(state, initialState),
    [state, initialState],
  );

  const valid = useMemo(
    () => Boolean(state.title && state.author),
    [state.title, state.author],
  );

  return {
    author: state.author,
    title: state.title,
    setAuthor: (author) => setState({ ...state, author }),
    setTitle: (title) => setState({ ...state, title }),
    dirty,
    valid,
  };
}

// naive deep equality check because I don't want to pull in lodash
// don't use this for large objects
function isEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

ProjectDetailsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};
